import { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';

// Helper function to count words in non-LaTeX text
function countWordsInNonLatex(text: string): number {
  if (!text) return 0;
  text = text.trim();
  if (text === '') return 0;
  
  // Count CJK characters (each character is considered one word)
  const cjkMatches = text.match(/[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/g);
  const cjkCount = cjkMatches ? cjkMatches.length : 0;
  
  // Remove CJK characters and count English words and numbers
  const textWithoutCJK = text.replace(/[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/g, ' ');
  const englishMatches = textWithoutCJK.match(/\b[a-zA-Z]+(?:['\-][a-zA-Z]+)*\b|\b\d+(?:[.,]\d+)*\b/g);
  const englishCount = englishMatches ? englishMatches.length : 0;
  
  return cjkCount + englishCount;
}

// Helper function to count meaningful content in LaTeX formulas
function countWordsInLatex(latexText: string): number {
  if (!latexText) return 0;
  
  // Remove LaTeX delimiters ($ and $$)
  let content = latexText.replace(/^\$+|\$+$/g, '');
  
  // Remove common LaTeX commands and their arguments
  // This regex matches \command{...} patterns and standalone commands
  content = content.replace(/\\[a-zA-Z]+\{[^}]*\}/g, ' ');
  content = content.replace(/\\[a-zA-Z]+/g, ' ');
  
  // Remove structural characters but keep meaningful content
  content = content.replace(/[{}^_=+\-*/()[\]|<>]/g, ' ');
  
  // Remove excessive whitespace
  content = content.replace(/\s+/g, ' ').trim();
  
  if (content === '') return 0;
  
  // Count remaining meaningful content (variables, numbers, text)
  // This includes single letters (variables), numbers, and words
  const meaningfulMatches = content.match(/\b[a-zA-Z0-9]+\b/g);
  return meaningfulMatches ? meaningfulMatches.length : 0;
}

// Main word counting function that handles both regular text and LaTeX
export function countWords(text: string): number {
  if (!text) return 0;
  text = text.trim();
  if (text === '') return 0;
  
  let totalWords = 0;
  let lastIndex = 0;
  
  // Find all LaTeX formulas and process text segments
  const latexRegex = /\$\$[\s\S]*?\$\$|\$[^$\n]*?\$/g;
  let match;
  
  while ((match = latexRegex.exec(text)) !== null) {
    // Count words in text before this LaTeX formula
    const textBefore = text.slice(lastIndex, match.index);
    totalWords += countWordsInNonLatex(textBefore);
    
    // Count meaningful content in the LaTeX formula
    const latexFormula = match[0];
    totalWords += countWordsInLatex(latexFormula);
    
    lastIndex = match.index + match[0].length;
  }
  
  // Count words in remaining text after the last LaTeX formula
  const remainingText = text.slice(lastIndex);
  totalWords += countWordsInNonLatex(remainingText);
  
  return totalWords;
}

// Validate text content without modifying it
function validateInputContent(
  text: string, 
  options: {
    maxPunctuationRatio?: number;
    maxConsecutivePunctuation?: number;
  } = {}
): string | null {
  if (!text) return null;

  const {
    maxPunctuationRatio = 0.5,
    maxConsecutivePunctuation = 20
  } = options;

  // Remove LaTeX formulas before checking punctuation
  const textWithoutLatex = text.replace(/\$\$[\s\S]*?\$\$|\$[^$\n]*?\$/g, ' ');
  
  // Check for excessive consecutive punctuation (excluding LaTeX)
  const consecutivePunctuationRegex = new RegExp(`[^\\w\\s\\u4e00-\\u9fff\\u3040-\\u309f\\u30a0-\\u30ff\\uac00-\\ud7af]{${maxConsecutivePunctuation + 1},}`, 'g');
  if (consecutivePunctuationRegex.test(textWithoutLatex)) {
    return `Text contains too many consecutive punctuation marks (max ${maxConsecutivePunctuation}). Please use normal punctuation.`;
  }
  
  // Calculate punctuation ratio (excluding LaTeX)
  const punctuationCount = (textWithoutLatex.match(/[^\w\s\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/g) || []).length;
  const totalChars = textWithoutLatex.length;
  
  if (totalChars > 0 && punctuationCount / totalChars > maxPunctuationRatio) {
    return `Text contains too much punctuation (max ${Math.round(maxPunctuationRatio * 100)}%). Please provide meaningful content with normal punctuation usage.`;
  }
  
  return null;
}

// Clean text for submission (only called when data is being sent)
export function cleanTextForSubmission(text: string): string {
  if (!text) return '';
  
  // Only remove the most problematic control characters
  const cleanedText = text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Use DOMPurify for additional safety
  return DOMPurify.sanitize(cleanedText);
}

export interface UseValidatedTextareaOptions {
  initialValue?: string;
  minWords?: number;
  maxWords?: number;
  maxChars?: number;
  maxPunctuationRatio?: number;
  maxConsecutivePunctuation?: number;
  allowLatexCounting?: boolean;
  placeholder?: string;
  required?: boolean;
}

export interface UseValidatedTextareaReturn {
  value: string;
  wordCount: number;
  charCount: number;
  error: string | null;
  isValid: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  validate: () => boolean;
  setValue: (value: string) => void;
  clearError: () => void;
}

export function useValidatedTextarea(options: UseValidatedTextareaOptions = {}): UseValidatedTextareaReturn {
  const {
    initialValue = '',
    minWords = 0,
    maxWords = Infinity,
    maxChars = 10000,
    maxPunctuationRatio = 0.5,
    maxConsecutivePunctuation = 20,
    allowLatexCounting = true,
    required = false
  } = options;

  // Store the original user input without any modifications
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const wordCount = allowLatexCounting ? countWords(value) : countWordsInNonLatex(value);
  const charCount = value.length;

  const validateText = useCallback((text: string, isRealTimeValidation = false): { isValid: boolean; error: string | null } => {
    if (!text.trim() && required) {
      return { isValid: false, error: 'This field is required.' };
    }

    if (!text.trim() && !required) {
      return { isValid: true, error: null };
    }

    // Validate content without modifying it
    const contentError = validateInputContent(text, {
      maxPunctuationRatio,
      maxConsecutivePunctuation
    });

    if (contentError) {
      return { isValid: false, error: contentError };
    }

    const currentWordCount = allowLatexCounting ? countWords(text) : countWordsInNonLatex(text);

    // For real-time validation, be more lenient
    if (isRealTimeValidation) {
      if (currentWordCount > maxWords) {
        return { isValid: false, error: `Maximum ${maxWords} words allowed.` };
      }
      // Don't check minimum words during real-time validation
      return { isValid: true, error: null };
    }

    // For final validation, be strict
    if (currentWordCount < minWords) {
      return { isValid: false, error: `Minimum ${minWords} words required.` };
    }

    if (currentWordCount > maxWords) {
      return { isValid: false, error: `Maximum ${maxWords} words allowed.` };
    }

    return { isValid: true, error: null };
  }, [minWords, maxWords, maxPunctuationRatio, maxConsecutivePunctuation, allowLatexCounting, required]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Enforce character limit
    if (newValue.length > maxChars) {
      return;
    }
    
    // Store the original user input without any modifications
    setValue(newValue);
    
    // Validate the input and set error state
    const { error: validationError } = validateText(newValue, true);
    setError(validationError);
  }, [maxChars, validateText]);

  const validate = useCallback((): boolean => {
    const { isValid, error: validationError } = validateText(value, false);
    setError(validationError);
    return isValid;
  }, [value, validateText]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setValueDirectly = useCallback((newValue: string) => {
    setValue(newValue);
    const { error: validationError } = validateText(newValue, true);
    setError(validationError);
  }, [validateText]);

  // Determine if current state is valid (for real-time feedback)
  const { isValid } = validateText(value, true);

  return {
    value,
    wordCount,
    charCount,
    error,
    isValid,
    onChange,
    validate,
    setValue: setValueDirectly,
    clearError
  };
}